
import  { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useAppContext } from "../context/AppContext";
import AxiosInstance from "../context/axiosInstance"; 
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CalendarX, Clock } from "lucide-react";
import ConflictWarningModal from "./ConflictWarningModal";
import moment from "moment";

const SlotBookingModal = ({ service, onClose }) => {
  const { accessToken, userID } = useAppContext();

  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);

  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  useEffect(() => {
    const fetchSlots = async () => {
      // ✅ Use accessToken for the check
      if (!accessToken) {
        toast.error("You are not authorized.");
        onClose();
        return;
      }
      setLoading(true);
      setSelectedSlot(null);
      try {
        // ✅ 4. Use the global `AxiosInstance` instance for the request
        const { data } = await AxiosInstance.post(
          `/services/${service.service_id}/slots/`,
          {
            owner_id: service.partner_id,
            owner_type: "partner",
            date,
            service_duration: service.duration_minutes,
            location_mode: service.location_mode,
          }
        );
        setSlots(data.slots || []);
      } catch (err) {
        console.error("Fetch slots error:", err);
        toast.error("Failed to fetch available slots.");
        setSlots([]);
      } finally {
        setLoading(false);
      }
    };

    if (service && date) {
      fetchSlots();
    }
    // ✅ Removed AxiosInstance from the dependency array as it's a stable import now.
  }, [date, service, accessToken, onClose]);

const finalizeBooking = async () => {
  // We no longer use toast.promise.
  // The gatekeeper function (`handleBookingAttempt`) will manage the main 'booking' loading state.
  // We show a loading toast manually for this specific final step.
  const bookingToast = toast.loading("Finalizing your booking...");

  try {
    // Make the API call directly
    await AxiosInstance.post("/services/bookings/", {
      user_id: userID,
      partner_id: service.partner_id,
      service_id: service.service_id,
      date,
      start_time: selectedSlot.start_time,
      end_time: selectedSlot.end_time,
    });

    // If the call succeeds, update the toast to show success
    toast.success(`Booking confirmed for ${selectedSlot.start_time}!`, {
      id: bookingToast,
    });

    // Close the modal after a successful booking
    onClose();
  } catch (err) {
    // This block now runs for any non-2xx response
    console.error("Booking Finalization Error:", err);

    // ✅ Check for our specific 409 conflict status code
    if (err.response?.status === 409) {
      // Show a specific, helpful error message for this case
      toast.error(
        err.response.data.error || "You have already booked this slot.",
        {
          id: bookingToast,
          icon: "❗", // A warning icon
        }
      );
    } else {
      // For all other errors, show a generic failure message
      toast.error(
        err.response?.data?.error || "Booking failed. Please try again.",
        {
          id: bookingToast,
        }
      );
    }
  }
};
const handleBookingAttempt = async () => {
  if (!selectedSlot) return toast.error("Please select a time slot.");
  if (!accessToken || !userID) return toast.error("You are not authorized.");

  setBooking(true); // Start the main button's spinner
  try {
    const startTime = moment(
      `${date} ${selectedSlot.start_time}`,
      "YYYY-MM-DD HH:mm"
    )
      .utc()
      .toISOString();
    const endTime = moment(
      `${date} ${selectedSlot.end_time}`,
      "YYYY-MM-DD HH:mm"
    )
      .utc()
      .toISOString();

    const { data: conflictCheckData } = await AxiosInstance.post(
      "/timeline/conflicts/",
      {
        start_time: startTime,
        end_time: endTime,
      }
    );

    if (conflictCheckData.has_conflict) {
      setConflictData(conflictCheckData);
      setShowConflictModal(true);
      // We stop the spinner here because we are waiting for user input.
      setBooking(false);
    } else {
      // No conflict, proceed immediately.
      await finalizeBooking();
      // After finalizeBooking is done (success or error), stop the spinner.
      setBooking(false);
    }
  } catch (err) {
    toast.error(err.response?.data?.error || "Could not check for conflicts.");
    setBooking(false); // Ensure spinner stops on error
  }
};

  // ... (The rest of your JSX component remains the same, it's already well-structured)

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-md p-4 transition-all duration-300">
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.3, ease: "circOut" }}
        className="relative w-full max-w-lg bg-surface/90 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl shadow-black/50 p-8"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full p-2"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col">
          <h2 className="text-2xl font-bold tracking-tighter text-white mb-2">Book Your Slot</h2>
          <p className="text-gray-400 mb-8">
            Select a date and an available time for <span className="text-white font-medium">"{service.name}"</span>.
          </p>

          <div className="mb-6">
            <label
              htmlFor="booking-date"
              className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider"
            >
              Select Date
            </label>
            <input
              id="booking-date"
              type="date"
              value={date}
              min={new Date().toISOString().split("T")[0]} // Prevent booking past dates
              onChange={(e) => setDate(e.target.value)}
              className="w-full h-14 bg-white/5 border border-white/10 rounded-xl px-4 text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none transition-all hover:bg-white/10"
            />
          </div>

          <div className="min-h-[150px] pt-2 mb-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
                <Loader2 size={32} className="animate-spin mb-4 text-primary-500" />
                <p>Finding available slots...</p>
              </div>
            ) : slots.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-center py-8 bg-white/5 rounded-2xl border border-white/5 border-dashed">
                <CalendarX size={40} className="mb-4 opacity-50" />
                <p className="font-semibold text-white text-lg">No Slots Available</p>
                <p className="text-sm">Please try a different date.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {slots.map((slot, idx) => {
                  const isSelected = selectedSlot === slot;
                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-3 rounded-xl text-center transition-all duration-300 border ${
                        isSelected
                          ? "bg-white text-black font-bold scale-[1.02] shadow-lg border-white"
                          : "bg-surface text-gray-300 border-white/10 hover:bg-white/5 hover:border-white/20"
                      }`}
                    >
                      <span className="flex items-center justify-center gap-2 text-sm">
                        <Clock size={14} className={isSelected ? "text-black" : "text-primary-400"} /> {slot.start_time}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            onClick={handleBookingAttempt}
            disabled={!selectedSlot || booking || loading}
            className="w-full bg-white text-black font-bold py-4 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:scale-[1.02] gap-2"
          >
            {booking ? <Loader2 className="animate-spin" /> : "Confirm Booking"}
          </button>
        </div>
      </motion.div>
      <AnimatePresence>
        {showConflictModal && (
          <ConflictWarningModal
            newActionTitle={`Booking for "${service.name}"`}
            conflicts={conflictData.conflicting_items}
            onConfirmOverride={async () => {
              setShowConflictModal(false);
              await finalizeBooking(); // Call the worker function
            }}
            onCancel={() => setShowConflictModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
export default SlotBookingModal;
